import React, { useState, useEffect, useRef } from "react";
import { updateProgramTasks } from "../data/ccProgram";
import "../styles/CareJourney.css";

const REMIND_AHEAD = 2;

export default function CareJourney({ programData }) {
  /*
    programData shape (from single_patient_program):
    {
      tasklist_id, program_id, current_phase,
      phases: [
        {
          phase_id, phase_name, day_range: [d0, d1],
          sections: [
            {
              section, freq, on_days?,
              tasks: [{ instance_key, task_id, title, section, freq, program_day,
                        completed_status, flag_status, doctor_note }]
            }
          ]
        }
      ]
    }
  */

  const tasklistId    = programData?.tasklist_id;
  const phases        = programData?.phases || [];
  const totalDays     = 90;

  // ── which phase tab is open ───────────────────────────────────────────────
  const [activePhaseId, setActivePhaseId] = useState(programData?.current_phase || 1);

  // ── which day is selected ─────────────────────────────────────────────────
  const activePhase  = phases.find(p => p.phase_id === activePhaseId) || phases[0];
  const [d0, d1]     = activePhase?.day_range || [0, 0];
  const [activeDay,  setActiveDay]  = useState(d0);

  // ── flat task state keyed by instance_key ─────────────────────────────────
  const [taskState,   setTaskState]   = useState({});
  const [dirty,       setDirty]       = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saveError,   setSaveError]   = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [noteModal,   setNoteModal]   = useState(null);   // { instanceKey, note }

  const stripRef = useRef(null);

  // ── init state from API data ──────────────────────────────────────────────
  useEffect(() => {
    if (!phases.length) return;
    const initial = {};
    phases.forEach(ph =>
      ph.sections.forEach(sec =>
        sec.tasks.forEach(t => {
          initial[t.instance_key] = {
            completed_status: t.completed_status ?? false,
            flag_status:      t.flag_status      ?? false,
            doctor_note:      t.doctor_note      || "",
          };
        })
      )
    );
    setTaskState(initial);
    setDirty(false);
    setSaveSuccess(false);
  }, [programData]);

  // ── reset active day when switching phases ────────────────────────────────
  useEffect(() => {
    if (!activePhase) return;
    setActiveDay(activePhase.day_range[0]);
  }, [activePhaseId]);

  // ── scroll day strip to active button ────────────────────────────────────
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const btn = strip.querySelector(".cc-day-btn.active");
    if (btn) {
      const offset = btn.offsetLeft - strip.offsetWidth / 2 + btn.offsetWidth / 2;
      strip.scrollLeft = offset;
    }
  }, [activeDay, activePhaseId]);

  const markDirty = () => { setDirty(true); setSaveSuccess(false); };

  const toggleComplete = (key) => {
    setTaskState(p => ({ ...p, [key]: { ...p[key], completed_status: !p[key]?.completed_status } }));
    markDirty();
  };

  const toggleFlag = (key, e) => {
    e.stopPropagation();
    setTaskState(p => ({ ...p, [key]: { ...p[key], flag_status: !p[key]?.flag_status } }));
    markDirty();
  };

  const openNote  = (key, e) => {
    e.stopPropagation();
    setNoteModal({ instanceKey: key, note: taskState[key]?.doctor_note || "" });
  };

  const saveNote  = () => {
    if (!noteModal) return;
    setTaskState(p => ({
      ...p,
      [noteModal.instanceKey]: { ...p[noteModal.instanceKey], doctor_note: noteModal.note },
    }));
    markDirty();
    setNoteModal(null);
  };

  // ── save — sends only today's task instances to the backend ─────────────
  const handleSave = async () => {
    if (!dirty || !tasklistId) {
      if (!tasklistId) setSaveError("No tasklist ID — cannot save.");
      return;
    }
    setSaving(true);
    setSaveError(null);

    // Day 0 (Phase 1 onboarding) — keys have no __d suffix (freq="once").
    // All other days — only send keys ending in __d{activeDay}.
    // This prevents Phase 1 keys leaking into Day 5 saves and causing 400s.
    const isPhaseOne = activeDay === 0;
    const updates = Object.entries(taskState)
      .filter(([key]) =>
        isPhaseOne
          ? !key.includes("__d")
          : key.endsWith(`__d${activeDay}`)
      )
      .map(([key, v]) => ({
        task_id:          key,
        completed_status: v.completed_status,
        flag_status:      v.flag_status,
        doctor_note:      v.doctor_note,
      }));

    try {
      await updateProgramTasks({
        tasklistId,
        currentPhase: activePhaseId,
        programDay:   activeDay,
        updates,
      });
      setDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Save error:", err);
      setSaveError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── derived data for the selected day ────────────────────────────────────
  const sectionsForDay = (phase, day) => {
    if (!phase) return [];
    const [d0] = phase.day_range;
    return phase.sections.filter(sec => {
      if (sec.freq === "once" || sec.freq === "daily") return true;
      if (sec.freq === "every3days") return (day - d0) % 3 === 0;
      if (sec.freq === "milestone")  return sec.on_days?.includes(day);
      return false;
    });
  };

  const remindersForDay = (day) => {
    const result = [];
    phases.forEach(ph =>
      ph.sections.forEach(sec => {
        if (sec.freq !== "milestone") return;
        (sec.on_days || []).forEach(onDay => {
          const daysAway = onDay - day;
          if (daysAway > 0 && daysAway <= REMIND_AHEAD) {
            result.push({
              section:       sec.section,
              remindForDay:  onDay,
              daysAway,
              taskTitles:    sec.tasks.slice(0, 2).map(t => t.title),
              total:         sec.tasks.length,
            });
          }
        });
      })
    );
    return result;
  };

  const dayStats = (phase, day) => {
    const secs = sectionsForDay(phase, day);
    let total = 0, done = 0;
    secs.forEach(sec =>
      sec.tasks
        .filter(t => t.program_day === day)
        .forEach(t => {
          total++;
          if (taskState[t.instance_key]?.completed_status) done++;
        })
    );
    return { total, done, pct: total ? Math.round(done / total * 100) : 100 };
  };

  const isDayMissed = (phase, day, todayDay) => {
    if (day >= todayDay) return false;
    const { total, done } = dayStats(phase, day);
    return total > 0 && done < total;
  };

  const hasUpcomingReminder = (day) => remindersForDay(day).length > 0;

  // ── treat Day 0 as "today" for demo; in production pass in real program day
  const TODAY_PROGRAM_DAY = programData?.today_program_day ?? activeDay;

  const activeSections = sectionsForDay(activePhase, activeDay);
  const reminders      = remindersForDay(activeDay);
  const { total, done, pct } = dayStats(activePhase, activeDay);

  if (!phases.length) {
    return (
      <div className="cc-journey" style={{ padding: "40px 24px", color: "#94a3b8", textAlign: "center" }}>
        No programme data available.
      </div>
    );
  }

  return (
    <div className="cc-journey">

      {/* ── Phase tabs ── */}
      <div className="cc-phase-tabs">
        {phases.map(ph => (
          <button
            key={ph.phase_id}
            className={`cc-phase-tab ${ph.phase_id === activePhaseId ? "active" : ""}`}
            onClick={() => setActivePhaseId(ph.phase_id)}
          >
            <span className="cc-ptab-num">{ph.phase_id}</span>
            <span className="cc-ptab-label">{ph.phase_name}</span>
            <span className="cc-ptab-range">
              {ph.day_range[0] === ph.day_range[1]
                ? `Day ${ph.day_range[0]}`
                : `Days ${ph.day_range[0]}–${ph.day_range[1]}`}
            </span>
          </button>
        ))}
      </div>

      {/* ── Day strip (multi-day phases only) ── */}
      {d1 > d0 && (
        <div className="cc-day-nav">
          <span className="cc-day-nav-label">Day</span>
          <div className="cc-day-strip" ref={stripRef}>
            {Array.from({ length: d1 - d0 + 1 }, (_, i) => d0 + i).map(day => {
              const complete = dayStats(activePhase, day).pct === 100 &&
                               dayStats(activePhase, day).total > 0;
              const missed   = isDayMissed(activePhase, day, TODAY_PROGRAM_DAY);
              const remind   = hasUpcomingReminder(day) && !missed;
              return (
                <button
                  key={day}
                  className={[
                    "cc-day-btn",
                    day === activeDay ? "active" : "",
                    complete && day !== activeDay ? "complete" : "",
                    missed   && day !== activeDay ? "missed"   : "",
                    remind   && day !== activeDay ? "remind"   : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => setActiveDay(day)}
                >
                  {day}
                  <span className="cc-day-dot-row">
                    {missed  && day !== activeDay && <span className="cc-dot cc-dot-red"   />}
                    {remind  && day !== activeDay && <span className="cc-dot cc-dot-amber" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Header + Save ── */}
      <div className="cc-journey-header">
        <div>
          <h2 className="cc-journey-phase-title">
            {activeDay === 0 ? "Day 0 — Onboarding" : `Day ${activeDay}`}
          </h2>
          <p className="cc-journey-weeks">
            {activeSections.length} section{activeSections.length !== 1 ? "s" : ""} · {total} tasks today
          </p>
        </div>
        <div className="cc-journey-header-right">
          <div className="cc-journey-pct-badge">{pct}% complete</div>
          <button
            className={[
              "cc-save-btn",
              dirty       ? "active"  : "",
              saving      ? "saving"  : "",
              saveSuccess ? "success" : "",
            ].filter(Boolean).join(" ")}
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : saveSuccess ? "✓ Saved" : "Save Changes"}
          </button>
        </div>
      </div>

      {saveError && <div className="cc-save-error">{saveError}</div>}

      {/* ── Progress bar ── */}
      <div className="cc-journey-progress-bar">
        <div className="cc-journey-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      {/* ── Reminder cards ── */}
      {reminders.map((r, i) => (
        <div key={i} className="cc-reminder-card">
          <div className="cc-reminder-icon">
            <svg viewBox="0 0 12 12" fill="none" width="12" height="12">
              <circle cx="6" cy="6" r="5" stroke="#854F0B" strokeWidth="1.2" />
              <line x1="6" y1="3.5" x2="6" y2="6.5" stroke="#854F0B" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="6" cy="8.3" r=".7" fill="#854F0B" />
            </svg>
          </div>
          <div className="cc-reminder-body">
            <div className="cc-reminder-title">
              In {r.daysAway} day{r.daysAway > 1 ? "s" : ""} — {r.section}
            </div>
            <div className="cc-reminder-desc">
              {r.taskTitles.join(" · ")}{r.total > 2 ? ` +${r.total - 2} more` : ""}
            </div>
          </div>
        </div>
      ))}

      {/* ── Task list grouped by section ── */}
      <div className="cc-task-list">
        {activeSections.length === 0 && (
          <div className="cc-empty-day">No tasks scheduled for this day.</div>
        )}

        {activeSections.map(sec => {
          const dayTasks = sec.tasks.filter(t => t.program_day === activeDay);
          if (!dayTasks.length) return null;

          return (
            <div key={sec.section} className="cc-section-block">
              <div className="cc-section-label">
                {sec.section}
                {sec.freq === "milestone" && (
                  <span className="cc-badge cc-badge-milestone">milestone</span>
                )}
              </div>

              {dayTasks.map(task => {
                const s       = taskState[task.instance_key] || {};
                const done    = s.completed_status;
                const flagged = s.flag_status;
                const note    = s.doctor_note;
                const missed  = isDayMissed(activePhase, activeDay, TODAY_PROGRAM_DAY) && !done;

                return (
                  <div
                    key={task.instance_key}
                    className={[
                      "cc-task-item",
                      done                              ? "done"      : "",
                      flagged                           ? "flagged"   : "",
                      missed                            ? "missed"    : "",
                      sec.freq === "milestone" && !done ? "milestone" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => toggleComplete(task.instance_key)}
                  >
                    <div className={`cc-task-check ${done ? "checked" : ""}`}>
                      {done && (
                        <svg viewBox="0 0 10 8" fill="none" width="10" height="10">
                          <polyline points="1,4 4,7 9,1" stroke="#fff" strokeWidth="1.8"
                                    strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>

                    <div className="cc-task-body">
                      <div className="cc-task-text">{task.title}</div>
                      {note && <div className="cc-task-note-preview">📝 {note}</div>}
                      {missed && !done && (
                        <span className="cc-badge cc-badge-missed">missed</span>
                      )}
                    </div>

                    <div className="cc-task-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className={`cc-task-icon-btn ${note ? "has-note" : ""}`}
                        title={note ? "Edit note" : "Add note"}
                        onClick={e => openNote(task.instance_key, e)}
                      >✏️</button>
                      <button
                        className={`cc-task-icon-btn ${flagged ? "flagged" : ""}`}
                        title={flagged ? "Remove flag" : "Flag task"}
                        onClick={e => toggleFlag(task.instance_key, e)}
                      >{flagged ? "🚩" : "⚑"}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── Note modal ── */}
      {noteModal && (
        <div className="cc-note-overlay" onClick={() => setNoteModal(null)}>
          <div className="cc-note-modal" onClick={e => e.stopPropagation()}>
            <div className="cc-note-header">
              <h3 className="cc-note-title">Doctor's Note</h3>
              <button className="cc-note-close" onClick={() => setNoteModal(null)}>✕</button>
            </div>
            <textarea
              className="cc-note-textarea"
              placeholder="Add a clinical note for this task…"
              value={noteModal.note}
              onChange={e => setNoteModal(p => ({ ...p, note: e.target.value }))}
              autoFocus
              rows={5}
            />
            <div className="cc-note-footer">
              <button className="cc-note-cancel" onClick={() => setNoteModal(null)}>Cancel</button>
              <button className="cc-note-save"   onClick={saveNote}>Save Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
